/**
 * THE SHEETS OF HER ASKS ABOUT HER REGULAR CLEANS (round five, slice C3, ops's section 12.4 of its
 * revision of 2026-10-17, live on both of its hosts): "Ask to pause", "Ask to resume", "Ask to
 * change" and "Ask to end", each opened from a home's regular cleans on Account or on Your agreement
 * (`CleansAsks.tsx`). No artboard draws them, so each is built from drawn parts and goes on business
 * issue 13's list: Schedule's sheet (`Sheet.tsx`), Feedback's chips and field, and Schedule's "Send
 * the request" and the first sentence of the line under it. The words are `CLEANS` in `words.ts`.
 *
 * - WHAT EACH SENDS is what ops takes, as the request door reads it (`api/src/seam/requests.ts`):
 *   the home; a pause's first day and its last, or `null` for no return date yet; a resume's day; a
 *   change's cadence, weekday and window in her words, each `null` to keep it, at least one changed,
 *   and its first day; an ending's service and last day; and her note only where she wrote one.
 *   A cadence or a weekday that stands is never offered as a change, and a paused home is offered no
 *   new cadence, which ops refuses (`409 already_paused`): the sheet says to call for one.
 * - DAYS TODAY OR LATER, AND WITHIN 365 DAYS, day 365 included, today being the place's own day:
 *   ops takes those alone (`CLEANS_DAYS_AHEAD`), and an until not before its from, so Send waits
 *   for them; each date field is bounded by them, Until starting from the day the pause does, and
 *   a day typed outside them is said under its field, marked and described by what to change, never
 *   left to a grey Send alone (round six, slice S2).
 * - ASKING IS ALL IT DOES (CLAUDE.md binding rule 1). A request the door has written closes the
 *   sheet, and the home says it was sent until ops says more; nothing is drawn paused, changed or
 *   ended until ops pushes it so. A new cadence's price is the office's to say, in its message.
 * - ONE REQUEST HOWEVER OFTEN IT IS TAPPED, AND HOWEVER OFTEN THE SHEET IS CLOSED: a key is minted
 *   once for each answer, and while one is out another tap does nothing. Both are held by the page
 *   for the home and the ask (`lib/asks.ts`), never by the sheet.
 * - WHAT THE DOOR ANSWERS decides what follows: sent is the page's to show; ops's own refusals,
 *   `no_schedule`, `already_paused` and `schedule_not_paused`, are said here in ops's words with the
 *   office's number as a link, take the focus, and have the page read itself again, since what it
 *   offered no longer stands; a code in her note or her window is named, keeping what she wrote; an
 *   ask of its type that already waits closes the sheet and reads the page again, which says it; a
 *   404, the home gone or not hers to ask about now, which the door answers alike, says these
 *   cleans can no longer be changed here, with the phone, in the same words either way, takes the
 *   focus and reads the page again, and the sheet stays open on it even where the page read again
 *   no longer holds the home (`CleansAsks.tsx` keeps it drawn), never closing in silence (the
 *   owner, 2026-10-20); a 401 is the way to the door; past the door's limits and anything else say
 *   so with the phone and leave the sheet as it was to try again.
 * - A DIALOG, drawn in the sheet: named by its words, which take the focus when it opens; the page
 *   behind it inert; closed on Escape, on its X, or on a tap outside it. Under its name, the home's
 *   label where the page names several, and its regular cleans only where one is listed: never "No
 *   regular cleans.", which read as what the ask was about.
 * - AN ENDING THAT ENDS NOTHING THAT REPEATS (the fifth client-journey walk, 2026-10-20), as at a
 *   home whose only live agreement is a one-time clean (`cancelOf`): named for what it cancels,
 *   "Ask to cancel house cleaning", with "One time." under it, and its day "Cancel every clean
 *   after", ops's `last_day` as ops reads it. The same `service.end`; nothing else changes. Where
 *   the home has an open visit of it, the name says that visit by its kind and its day, "Ask to
 *   cancel the deep clean on Tuesday 27 October" (`askWords`, ops's answer on ops #76, 2026-10-22).
 * - A CANCEL THAT NAMES ITS CLEAN CANCELS THAT CLEAN (the review of 2621dcd, where the sheet named
 *   27 October and sent the day she typed, 4 November, which kept it). The Worker names a visit
 *   only where it is the one open one-time visit of the service after today. For the service
 *   chosen, where one is named, the sheet asks no day: one line says which clean the office is asked
 *   to cancel (`cancelVisitOf`, `cancelVisitAsked`), and ops's `last_day` is the day before it, so
 *   the office ends the agreement before that clean. Her note as ever. Where none is named, or the
 *   day before is past the days ops takes, the sheet is as it was.
 */
import {
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { sendRequest, type CleansAskType, type HomeCleans, type TooMany } from '../api.ts';
import { keyFor, type Asking } from '../lib/asks.ts';
import {
  askWords,
  cancelOf,
  cancelVisitOf,
  cleansLine,
  pausedHome,
  repeatsListed,
} from '../lib/cleans.ts';
import { CLEANS_DAYS_AHEAD, dayInWords, daysAfter, fullDate } from '../lib/dates.ts';
import {
  AGREEMENT,
  cadenceChoice,
  CLEANS,
  dayFromTodayTo,
  EDIT_HOME,
  FAILED_BEFORE_PHONE,
  isScheduleRefusal,
  LOOKS_LIKE_A_CODE,
  SCHEDULE,
  SCHEDULE_REFUSAL_WORDS,
  serviceName,
  tooManyBeforePhone,
  untilOnOrAfter,
  weekdayChoice,
  type ScheduleRefusal,
} from '../words.ts';
import { NAVY_BUTTON } from './buttons.ts';
import { Check } from './MoveSheet.tsx';
import { Phone } from './Phone.tsx';
import { SaidWithPhone } from './SaidWithPhone.tsx';
import { Sheet } from './Sheet.tsx';

/** The longest note the door takes, and the longest window: ops's section 12.4. */
export const CLEANS_NOTE_MAX = 500;
export const CLEANS_WINDOW_MAX = 100;

/** The cadences a change may name, in ops's order, and the weekdays, Monday first. */
const CADENCES = ['weekly', 'every_2_weeks', 'every_4_weeks'] as const;
const WEEKDAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

/** The choice that keeps what stands, sent as `null`. */
const KEEP = '';

/** What the sheet says after an ask that was not sent. */
type Problem =
  | { readonly code: 'note' | 'window' | null }
  | { readonly refusal: ScheduleRefusal }
  /** Past the door's own limits, with whose count and when, as the door said (slice S1). */
  | { readonly tooMany: TooMany | undefined }
  /** The home is no longer one she may ask about here: the door's 404, gone or not hers alike. */
  | 'not_here'
  | 'failed';

const QUESTION = 'text-body font-semibold text-text-body';
const HINT = 'text-small text-pretty text-text-muted';

/** Feedback's field. */
const FIELD =
  'mt-2 field-sizing-content max-h-48 min-h-tap-call resize-none rounded-control border-2 ' +
  'border-border-strong bg-surface-raised px-4 py-3 text-body text-text-body';

/** A one-line field, a day or her window, as tall as Feedback's. */
const LINE_FIELD =
  'mt-2 min-h-tap-call w-full rounded-control border-2 border-border-strong bg-surface-raised ' +
  'px-4 text-body text-text-body disabled:opacity-60';

/** The focus ring, drawn on a chip while its hidden radio button has it, as Feedback draws it. */
const RING =
  'has-focus-visible:outline-3 has-focus-visible:outline-offset-2 has-focus-visible:outline-focus-ring';

const CHIP =
  'flex min-h-tap cursor-pointer items-center gap-1.5 rounded-control border-2 px-4 text-small';
const CHIP_CHOSEN = 'border-surface-brand bg-surface-brand-soft font-semibold text-text-body';
const CHIP_FREE =
  'border-border-strong bg-surface-raised text-text-body hover:bg-surface-accent-soft';

export interface CleansSheetProps {
  readonly type: CleansAskType;
  readonly home: HomeCleans;
  /** Whether the page names each home, as it does when there are several, so the sheet says which. */
  readonly named: boolean;
  /** The business's day, from the place, which the days are counted from. */
  readonly today: string;
  /** The household the place shows, which the request is about. */
  readonly household: string;
  /** Whether a request of this ask about the home is out, and its keys, held by the page. */
  readonly asking: Asking;
  /** Closes the sheet, having asked nothing more. */
  readonly onClose: () => void;
  /** The door wrote the request, whose id this is. */
  readonly onSent: (id: string) => void;
  /** The session has ended. */
  readonly onSignedOut: () => void;
  /** What the page shows may be out of date: read it again. */
  readonly onStale: () => void;
}

/**
 * A day of the sheet, a date field with the bounds ops takes, from `first`, today unless a later day
 * bounds it, to `last`; and under it, in words, what to change where the day typed is one ops would
 * refuse, which the field is then marked and described by (round six, slice S2).
 */
function DayField({
  id,
  label,
  value,
  onChange,
  first,
  last,
  fix = null,
  disabled = false,
}: {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (day: string) => void;
  readonly first: string;
  readonly last: string;
  /** What to change in the day typed, said under it, or null. */
  readonly fix?: string | null;
  readonly disabled?: boolean;
}) {
  const fixId = useId();
  const said = disabled ? null : fix;
  return (
    <div className="mt-5 flex flex-col">
      <label htmlFor={id} className={QUESTION}>
        {label}
      </label>
      <input
        id={id}
        type="date"
        value={value}
        min={first}
        max={last}
        disabled={disabled}
        aria-invalid={said === null ? undefined : true}
        aria-describedby={said === null ? undefined : fixId}
        onChange={(event) => onChange(event.target.value)}
        className={LINE_FIELD}
      />
      <div aria-live="polite">
        {said === null ? null : (
          <p id={fixId} className="mt-2 text-small text-pretty text-text-body">
            {said}
          </p>
        )}
      </div>
    </div>
  );
}

/** Chips of which one is chosen, as Feedback's areas: a radio group named by its question. */
function Choices({
  question,
  name,
  options,
  chosen,
  onChoose,
}: {
  readonly question: string;
  readonly name: string;
  readonly options: readonly (readonly [string, string])[];
  readonly chosen: string | null;
  readonly onChoose: (value: string) => void;
}) {
  const questionId = useId();
  return (
    <>
      <p id={questionId} className={`mt-5 ${QUESTION}`}>
        {question}
      </p>
      <div role="radiogroup" aria-labelledby={questionId} className="mt-2.5 flex flex-wrap gap-2">
        {options.map(([value, words]) => {
          const on = chosen === value;
          return (
            <label key={value} className={`${CHIP} ${on ? CHIP_CHOSEN : CHIP_FREE} ${RING}`}>
              <input
                type="radio"
                name={name}
                value={value}
                checked={on}
                onChange={() => onChoose(value)}
                className="sr-only"
              />
              {on ? <Check size={18} /> : null}
              <span>{words}</span>
            </label>
          );
        })}
      </div>
    </>
  );
}

export function CleansSheet({
  type,
  home,
  named,
  today,
  household,
  asking,
  onClose,
  onSent,
  onSignedOut,
  onStale,
}: CleansSheetProps) {
  const fromId = useId();
  const untilId = useId();
  const windowId = useId();
  const windowHintId = useId();
  const noteId = useId();
  const problemId = useId();
  const cadenceGroup = useId();
  const weekdayGroup = useId();
  const serviceGroup = useId();

  const [from, setFrom] = useState('');
  const [until, setUntil] = useState('');
  const [noReturn, setNoReturn] = useState(false);
  const [cadence, setCadence] = useState(KEEP);
  const [weekday, setWeekday] = useState(KEEP);
  const [window, setWindow] = useState('');
  const [service, setService] = useState<string | null>(
    home.services.length === 1 ? home.services[0]! : null,
  );
  const [note, setNote] = useState('');
  const [problem, setProblem] = useState<Problem | null>(null);
  const noteField = useRef<HTMLTextAreaElement>(null);
  const windowField = useRef<HTMLInputElement>(null);
  const refusalLine = useRef<HTMLParagraphElement>(null);
  /** Whether this sheet is still open, and so has anything to say. */
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Ops's own refusal, or a home no longer hers to ask about here: what the page offered no longer
  // stands, so the page is read again and the button that had the focus may go. The focus goes to
  // what the sheet says, which stays, so it is read out and stays in the sheet, where Escape still
  // closes it.
  useEffect(() => {
    if (focused(problem)) refusalLine.current?.focus();
  }, [problem]);

  const last = daysAfter(today, CLEANS_DAYS_AHEAD) ?? today;
  const inRange = (day: string) => /^\d{4}-\d{2}-\d{2}$/.test(day) && day >= today && day <= last;
  const paused = pausedHome(home);
  const words = window.trim() === '' ? null : window;
  /** What to change in a day typed outside the days ops takes, or null for none typed or one it takes. */
  const outside = (day: string) =>
    day === '' || inRange(day) ? null : dayFromTodayTo(fullDate(last) ?? last);
  /** Until starts from the day the pause does, once that is one ops takes. */
  const untilFirst = type === 'schedule.pause' && inRange(from) ? from : today;
  const untilFix =
    outside(until) ??
    (untilFirst !== today && inRange(until) && until < untilFirst
      ? untilOnOrAfter(dayInWords(untilFirst) ?? untilFirst)
      : null);
  /**
   * The one clean a cancel of the service chosen names (`cancelVisitOf`): no day asked, the day
   * before it sent as the last day. Only where that day is one ops takes; where it is past day 365,
   * every day she may choose is before the clean, so the day is asked as before.
   */
  const visit = type === 'service.end' && service !== null ? cancelVisitOf(home, service) : null;
  const theClean = visit !== null && inRange(visit.lastDay) ? visit : null;

  /** The payload as ops reads it, or null while what is asked is not one ops takes. */
  function payload(): Readonly<Record<string, unknown>> | null {
    const noted = note.trim() === '' ? {} : { note };
    switch (type) {
      case 'schedule.pause':
        if (!inRange(from)) return null;
        if (noReturn) return { home_id: home.id, from, until: null, ...noted };
        return inRange(until) && until >= from ? { home_id: home.id, from, until, ...noted } : null;
      case 'schedule.resume':
        return inRange(from) ? { home_id: home.id, from, ...noted } : null;
      case 'schedule.change':
        if (!inRange(from) || (cadence === KEEP && weekday === KEEP && words === null)) return null;
        return {
          home_id: home.id,
          cadence: cadence === KEEP ? null : cadence,
          weekday: weekday === KEEP ? null : weekday,
          window: words,
          from,
          ...noted,
        };
      case 'service.end':
        if (service === null) return null;
        if (theClean !== null) {
          return { home_id: home.id, service, last_day: theClean.lastDay, ...noted };
        }
        return inRange(from) ? { home_id: home.id, service, last_day: from, ...noted } : null;
    }
  }
  const asked = payload();

  async function send(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (asking.out || asked === null) return;
    asking.out = true;
    setProblem(null);
    const answer = await sendRequest({
      key: keyFor(asking, JSON.stringify([type, asked])),
      type,
      household,
      payload: asked,
    });
    asking.out = false;
    switch (answer.outcome) {
      case 'sent':
        asking.keys.clear();
        onSent(answer.id);
        return;
      case 'signed_out':
        onSignedOut();
        return;
      // An ask of its type about the home already waits on the office: the page is read again, and
      // says where things stand.
      case 'already_asked':
        onStale();
        if (mounted.current) onClose();
        return;
      // The home is gone, or not hers to ask about now, which the door answers alike (binding rule
      // 2): said in the sheet, in the same words either way, never silent, and the page read again.
      case 'not_found':
        if (mounted.current) setProblem('not_here');
        onStale();
        return;
      case 'looks_like_a_code': {
        if (!mounted.current) return;
        const field = answer.field === 'note' || answer.field === 'window' ? answer.field : null;
        setProblem({ code: field });
        (field === 'window' ? windowField : field === 'note' ? noteField : null)?.current?.focus();
        return;
      }
      case 'refused':
        if (isScheduleRefusal(answer.code)) {
          if (mounted.current) setProblem({ refusal: answer.code });
          onStale();
          return;
        }
        if (mounted.current) setProblem('failed');
        return;
      case 'too_many':
        if (mounted.current) setProblem({ tooMany: answer.limit });
        return;
      default:
        if (mounted.current) setProblem('failed');
    }
  }

  const codeIn = (field: 'note' | 'window') =>
    problem !== null && typeof problem === 'object' && 'code' in problem && problem.code === field;

  // An ending that ends nothing that repeats cancels what it names (`cancelOf`): the sheet is named
  // for that, and says the agreement is one time under its name; "No regular cleans." is never said
  // under a sheet's name, where it read as what the ask was about (the fifth client-journey walk).
  const cancels = type === 'service.end' && cancelOf(home) !== null;
  const line = cancels ? `${AGREEMENT.oneTime}.` : repeatsListed(home) ? cleansLine(home) : null;
  const sub =
    (named && home.label !== undefined) || line !== null ? (
      <>
        {named && home.label !== undefined ? (
          <p className="text-body text-text-body">{home.label}</p>
        ) : null}
        {line === null ? null : <p className="text-small text-balance text-text-muted">{line}</p>}
      </>
    ) : undefined;

  return (
    <Sheet heading={askWords(type, home)} sub={sub} onClose={onClose}>
      <form method="post" onSubmit={(event) => void send(event)} className="mt-1 flex flex-col">
        {type === 'schedule.pause' ? (
          <>
            <DayField
              id={fromId}
              label={CLEANS.pauseFrom}
              value={from}
              onChange={setFrom}
              first={today}
              last={last}
              fix={outside(from)}
            />
            <DayField
              id={untilId}
              label={CLEANS.until}
              value={noReturn ? '' : until}
              onChange={setUntil}
              first={untilFirst}
              last={last}
              fix={untilFix}
              disabled={noReturn}
            />
            <label className="mt-2 flex min-h-tap cursor-pointer items-center gap-3 text-body text-text-body">
              <input
                type="checkbox"
                checked={noReturn}
                onChange={(event) => setNoReturn(event.target.checked)}
                className="size-5 shrink-0 accent-surface-brand"
              />
              {CLEANS.noReturnDate}
            </label>
          </>
        ) : null}

        {type === 'schedule.resume' ? (
          <DayField
            id={fromId}
            label={CLEANS.resumeFrom}
            value={from}
            onChange={setFrom}
            first={today}
            last={last}
            fix={outside(from)}
          />
        ) : null}

        {type === 'schedule.change' ? (
          <>
            {paused ? (
              <p className="mt-5 text-small text-pretty text-text-body">
                {CLEANS.pausedCadenceBeforePhone} <Phone after="." />
              </p>
            ) : (
              <>
                <Choices
                  question={CLEANS.howOften}
                  name={cadenceGroup}
                  options={[
                    [KEEP, CLEANS.keep],
                    ...CADENCES.filter((each) => each !== home.cadence).flatMap((each) => {
                      const words = cadenceChoice(each);
                      return words === null ? [] : [[each, words] as const];
                    }),
                  ]}
                  chosen={cadence}
                  onChoose={setCadence}
                />
                <p className={`mt-2 ${HINT}`}>{CLEANS.priceInMessage}</p>
              </>
            )}
            <Choices
              question={CLEANS.whichDay}
              name={weekdayGroup}
              options={[
                [KEEP, CLEANS.keep],
                ...WEEKDAYS.filter((each) => each !== home.weekday).flatMap((each) => {
                  const words = weekdayChoice(each);
                  return words === null ? [] : [[each, words] as const];
                }),
              ]}
              chosen={weekday}
              onChoose={setWeekday}
            />
            <div className="mt-5 flex flex-col">
              <label htmlFor={windowId} className={QUESTION}>
                {CLEANS.windowInYourWords}
              </label>
              <input
                ref={windowField}
                id={windowId}
                type="text"
                value={window}
                onChange={(event) => setWindow(event.target.value)}
                maxLength={CLEANS_WINDOW_MAX}
                aria-invalid={codeIn('window') ? true : undefined}
                aria-describedby={codeIn('window') ? `${windowHintId} ${problemId}` : windowHintId}
                className={LINE_FIELD}
              />
              <p id={windowHintId} className={`mt-1 ${HINT}`}>
                {CLEANS.windowKept}
              </p>
            </div>
            <DayField
              id={fromId}
              label={CLEANS.changeFrom}
              value={from}
              onChange={setFrom}
              first={today}
              last={last}
              fix={outside(from)}
            />
          </>
        ) : null}

        {type === 'service.end' ? (
          <>
            {home.services.length > 1 ? (
              <Choices
                question={CLEANS.whichService}
                name={serviceGroup}
                options={home.services.map((each) => [each, serviceName(each)] as const)}
                chosen={service}
                onChoose={setService}
              />
            ) : (
              <dl className="mt-5">
                <dt className={QUESTION}>{CLEANS.whichService}</dt>
                <dd className="mt-1 text-body text-text-body">
                  {serviceName(service ?? undefined)}
                </dd>
              </dl>
            )}
            {theClean === null ? (
              <DayField
                id={fromId}
                label={cancels ? CLEANS.cancelAfter : CLEANS.lastDay}
                value={from}
                onChange={setFrom}
                first={today}
                last={last}
                fix={outside(from)}
              />
            ) : (
              <p className="mt-5 text-body text-pretty text-text-body">{theClean.said}</p>
            )}
            <p className={`mt-2 ${HINT}`}>{CLEANS.mayCall}</p>
          </>
        ) : null}

        <div className="mt-5 flex flex-col">
          <label htmlFor={noteId} className={QUESTION}>
            {CLEANS.note}
          </label>
          <textarea
            ref={noteField}
            id={noteId}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={1}
            maxLength={CLEANS_NOTE_MAX}
            aria-invalid={codeIn('note') ? true : undefined}
            aria-describedby={codeIn('note') ? problemId : undefined}
            className={FIELD}
          />
        </div>

        <div aria-live="polite" id={problemId}>
          {problem === null ? null : <ProblemSaid problem={problem} refusalLine={refusalLine} />}
        </div>

        <button
          type="submit"
          disabled={asked === null}
          className={`mt-6 ${NAVY_BUTTON} disabled:cursor-not-allowed disabled:opacity-60`}
        >
          {SCHEDULE.send}
        </button>
      </form>
      <p className="mt-3 text-small text-pretty text-text-muted">{EDIT_HOME.nothingChanges}</p>
    </Sheet>
  );
}

/**
 * Whether what the sheet says takes the focus: ops's own refusal, or a home no longer hers to ask
 * about here, each of which the page read again may take the pressed button away from.
 */
const focused = (problem: Problem | null): boolean =>
  problem === 'not_here' ||
  (problem !== null && typeof problem === 'object' && 'refusal' in problem);

/** What the sheet says after an ask that was not sent. */
function ProblemSaid({
  problem,
  refusalLine,
}: {
  readonly problem: Problem;
  readonly refusalLine: RefObject<HTMLParagraphElement | null>;
}) {
  let said: ReactNode;
  if (problem === 'failed' || problem === 'not_here') {
    said = (
      <>
        {problem === 'not_here' ? CLEANS.notHereBeforePhone : FAILED_BEFORE_PHONE}{' '}
        <Phone after="." />
      </>
    );
  } else if ('tooMany' in problem) {
    said = (
      <>
        {tooManyBeforePhone(problem.tooMany)} <Phone after="." />
      </>
    );
  } else if ('refusal' in problem) {
    said = <SaidWithPhone words={SCHEDULE_REFUSAL_WORDS[problem.refusal]} />;
  } else {
    said = LOOKS_LIKE_A_CODE;
  }
  const takes = focused(problem);
  return (
    <p
      ref={takes ? refusalLine : undefined}
      tabIndex={takes ? -1 : undefined}
      className="mt-4 text-body text-pretty"
    >
      {said}
    </p>
  );
}
